import { useEffect, useRef } from 'react'
import { TELEGRAM_BOT_USERNAME } from '@/lib/telegram'

/** Forma exacta que Telegram entrega a `data-onauth` -- documentado en
 * https://core.telegram.org/widgets/login#receiving-authorization-data. `id` llega como
 * number ahí (a diferencia de `initData`, donde el `user` JSON también lo trae como number). */
export interface TelegramLoginWidgetUser {
  id: number
  first_name?: string
  last_name?: string
  username?: string
  photo_url?: string
  auth_date: number
  hash: string
}

declare global {
  interface Window {
    onTelegramAuth?: (user: TelegramLoginWidgetUser) => void
  }
}

/** Embebe el Telegram Login Widget oficial (P0.54, 29/08/2026, Sección 6/13 del encargo) --
 * único mecanismo usado para autenticar en navegador externo, nunca un protocolo propio.
 * Requiere que el bot tenga este dominio vinculado vía BotFather (`/setdomain`) -- sin eso,
 * Telegram muestra su propio error dentro del iframe del widget, antes de que este componente
 * o el backend intervengan. */
export function TelegramLoginWidget({ onAuth }: { onAuth: (user: TelegramLoginWidgetUser) => void }) {
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    window.onTelegramAuth = onAuth

    const script = document.createElement('script')
    script.src = 'https://telegram.org/js/telegram-widget.js?22'
    script.async = true
    script.setAttribute('data-telegram-login', TELEGRAM_BOT_USERNAME)
    script.setAttribute('data-size', 'large')
    script.setAttribute('data-radius', '12')
    script.setAttribute('data-onauth', 'onTelegramAuth(user)')
    script.setAttribute('data-request-access', 'write')

    const container = containerRef.current
    container?.appendChild(script)

    return () => {
      if (container) container.innerHTML = ''
      delete window.onTelegramAuth
    }
  }, [onAuth])

  return <div ref={containerRef} className="flex justify-center min-h-[46px]" />
}
